import { FormsyInput } from '@thanx/uikit/input'
import { FormsySelect } from '@thanx/uikit/select'
import { Switch } from '@thanx/uikit/switch'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { buildTranslate } from 'locales'
import { useMemo } from 'react'

import type { AppLink } from './AppFields'

import { Validations } from 'formsy-react/dist/interfaces'
import type { Fields as Merchant } from 'models/Merchant'
import type { FormModel } from 'models/smartLink'

const t = buildTranslate('cms.settings.smart_links.web_fallback')
const smartLinksT = buildTranslate('cms.settings.smart_links')

export type WebFallback = 'store' | 'ordering' | 'custom'
export type WebFallbackOptions = Array<{ label: string; value: WebFallback }>

export const appLinkToWebLocationMap: Record<AppLink, WebOrderingLocation> = {
  about: 'home-page',
  account: 'account',
  homePage: 'home-page',
  locations: 'home-page',
  notifications: 'home-page',
  nps: 'home-page',
  orderHistory: 'order-history',
  orderingMenu: 'ordering-menu',
  productDetails: 'items-on-the-menu',
  registeredCards: 'saved-cards',
  rewards: 'rewards',
  support: 'home-page',
  earn: 'rewards',
}

export const getWebFallbackOptions = (
  merchant: Merchant | null
): WebFallbackOptions => {
  const { web_ordering_enabled } = merchant || {}

  const webFallbackOptions: WebFallbackOptions = [
    { label: t('fallback_labels.app_store'), value: 'store' },
    { label: t('fallback_labels.web_ordering'), value: 'ordering' },
    { label: t('fallback_labels.custom_url'), value: 'custom' },
  ]

  if (web_ordering_enabled) {
    return webFallbackOptions
  }

  return webFallbackOptions.filter(option => option.value !== 'ordering')
}

export type WebOrderingLocation =
  | 'home-page'
  | 'rewards'
  | 'account'
  | 'saved-cards'
  | 'order-history'
  | 'ordering-menu'
  | 'items-on-the-menu'

export const defaultWebOrderingLocations: {
  label: string
  value: WebOrderingLocation
}[] = [
  {
    label: t('web_ordering_locations.home_page'),
    value: 'home-page',
  },
  {
    label: t('web_ordering_locations.rewards'),
    value: 'rewards',
  },
  {
    label: t('web_ordering_locations.account'),
    value: 'account',
  },
  {
    label: t('web_ordering_locations.saved_cards'),
    value: 'saved-cards',
  },
  {
    label: t('web_ordering_locations.order_history'),
    value: 'order-history',
  },
]

type Props = {
  className?: string
  formModel: FormModel
  initialFormModel: FormModel
  availableFallbacks: WebFallback[]
  name: string
  label: string
  menuCategoryOptions: { label: string; value: string }[]
  productOptions: { label: string; value: string }[]
  enforceCustomUrlValidation?: boolean
}

const WebFields = (props: Props) => {
  const {
    className,
    formModel,
    initialFormModel,
    availableFallbacks,
    name,
    label,
    menuCategoryOptions,
    productOptions,
    enforceCustomUrlValidation = false,
  } = props

  const merchant = useCurrentMerchant()

  const webFallbackOptions = getWebFallbackOptions(merchant)
  const fallbackOptions = webFallbackOptions.filter(o =>
    availableFallbacks.includes(o.value)
  )

  const webOrderingLocations = useMemo(() => {
    let locations = [...defaultWebOrderingLocations]

    if (menuCategoryOptions.length) {
      locations.push({
        label: t('web_ordering_locations.ordering_menu'),
        value: 'ordering-menu',
      })
    }

    if (productOptions.length) {
      locations.push({
        label: t('web_ordering_locations.items_on_the_menu'),
        value: 'items-on-the-menu',
      })
    }

    return locations
  }, [menuCategoryOptions, productOptions])

  const getFieldValue = ({
    fieldName,
    defaultValue,
  }: {
    fieldName: string
    defaultValue: string | null
  }) => formModel[fieldName] ?? defaultValue

  const getFromAppLink = (
    appLinkField: string,
    options: Array<{ value: string }>
  ): string | null => {
    const appLinkValue = formModel[appLinkField]
    const optionValues = options.map(option => option.value)
    return appLinkValue && optionValues.includes(appLinkValue)
      ? appLinkValue
      : null
  }

  const selectedAppLink = formModel['appLink']
  const webLocationFromAppLink = selectedAppLink
    ? appLinkToWebLocationMap[selectedAppLink]
    : null

  const orderingCategoryFromAppLink = getFromAppLink(
    'appLinkCategory',
    menuCategoryOptions
  )
  const productFromAppLink = getFromAppLink('appLinkProduct', productOptions)

  // A legacy +-encoded value (e.g. 'carrot+cake') matches no post-fix raw option
  // ('carrot cake'); seeding it would let a no-touch save re-emit the stale bytes
  // through generateWebOrderingUrl. Seed only on an exact option match so
  // isRequired forces a re-pick (the app-link-derived fallback is already guarded
  // by getFromAppLink).
  const storedWebOrderingProduct = initialFormModel[`${name}WebOrderingProduct`]
  const initialWebOrderingProduct =
    storedWebOrderingProduct &&
    productOptions.some(o => o.value === storedWebOrderingProduct)
      ? storedWebOrderingProduct
      : ''

  const selectedFallback = formModel[`${name}Fallback`]
  let customUrlValidation: Validations<string> = {
    isRequired: true,
  }
  let customUrlValidationErrors: Record<string, string> = {
    isRequired: smartLinksT('required'),
  }
  if (enforceCustomUrlValidation) {
    customUrlValidation = {
      ...customUrlValidation,
      isUrl: true,
    }
    customUrlValidationErrors = {
      ...customUrlValidationErrors,
      isUrl: smartLinksT('invalid_url'),
    }
  }

  return (
    <div className={className}>
      <FormsySelect
        label={label}
        name={`${name}Fallback`}
        value={initialFormModel[`${name}Fallback`] || ''}
        options={fallbackOptions}
        validations={{
          isRequired: true,
        }}
        validationErrors={{
          isRequired: buildTranslate('cms.settings.smart_links')('required'),
        }}
      />
      <Switch condition={selectedFallback === 'custom'}>
        <FormsyInput
          className="mt-m"
          name={`${name}CustomUrl`}
          value={initialFormModel[`${name}CustomUrl`] || ''}
          placeholder={t('custom_url_placeholder')}
          validations={customUrlValidation}
          validationErrors={customUrlValidationErrors}
        />
      </Switch>

      <Switch condition={selectedFallback === 'ordering'}>
        <FormsySelect
          aria-label={t('labels.select_ordering_location')}
          className="mt-s"
          name={`${name}WebOrderingLocation`}
          options={webOrderingLocations}
          validations={{
            isRequired: true,
          }}
          validationErrors={{
            isRequired: smartLinksT('required'),
          }}
          value={
            initialFormModel[`${name}WebOrderingLocation`] ||
            getFieldValue({
              fieldName: `${name}WebOrderingLocation`,
              defaultValue: webLocationFromAppLink,
            })
          }
        />
      </Switch>

      <Switch
        condition={
          selectedFallback === 'ordering' &&
          formModel[`${name}WebOrderingLocation`] === 'ordering-menu'
        }
      >
        <FormsySelect
          aria-label={t('labels.select_category')}
          className="mt-s"
          name={`${name}WebOrderingCategory`}
          placeholder={t('category_placeholder')}
          options={menuCategoryOptions}
          validations={{
            isRequired: true,
          }}
          validationErrors={{
            isRequired: smartLinksT('required'),
          }}
          value={
            initialFormModel[`${name}WebOrderingCategory`] ||
            getFieldValue({
              fieldName: `${name}WebOrderingCategory`,
              defaultValue: orderingCategoryFromAppLink,
            })
          }
        />
      </Switch>

      <Switch
        condition={
          selectedFallback === 'ordering' &&
          formModel[`${name}WebOrderingLocation`] === 'items-on-the-menu'
        }
      >
        <FormsySelect
          aria-label={t('labels.select_product')}
          className="mt-s"
          name={`${name}WebOrderingProduct`}
          placeholder={t('product_placeholder')}
          options={productOptions}
          validations={{
            isRequired: true,
          }}
          validationErrors={{
            isRequired: smartLinksT('required'),
          }}
          value={
            initialWebOrderingProduct ||
            getFieldValue({
              fieldName: `${name}WebOrderingProduct`,
              defaultValue: productFromAppLink,
            })
          }
        />
      </Switch>
    </div>
  )
}

export default WebFields
